import { Checkbox, Dialog, DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, makeStyles, MenuItem, TextField, Theme, useTheme } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PrevIcon from '@material-ui/icons/NavigateBefore';
import NextIcon from '@material-ui/icons/NavigateNext';
import { observer, Observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { OcDataview } from '../../../comps/OcDataview';
import { IEntity, IEntityStore, IHarmonizationRepo, ISupplier } from '../../../main/entities';
import { transl } from '../../../main/transl';
import { IEntityStateAgg } from '../../../main/useEntityStateAgg';
import { mg } from '../../../mg/mg';
import { useBooleanStateAgg, useStringStateAgg } from '../../../mg/useStateAgg';
import { DataviewStore } from '../../../stores/DataviewStore';
import { FilterRepo } from '../../../stores/internal/FilterRepo';
import { SortedRepo } from '../../../stores/internal/SortedRepo';
import { useOmnistore } from '../../../stores/Omnistore';
import { filterOnBrandFn } from './fiterOnBrandFn';
import { SupplierItemExtended, SupplierItemExtendedRepo } from './SupplierItemExtended';

const useStyles = makeStyles((theme: Theme) => ({
	harmoPageDialog: {
		'& .MuiDialog-paper': {
			width: 'calc(100% - 64px)',
			height: 'calc(100% - 64px)',
			maxWidth: 'unset',
			maxHeight: 'unset',
		},
	},
	closeButton: {
		position: 'absolute',
		right: theme.spacing(1),
		top: theme.spacing(1),
		color: theme.palette.grey[900],
	},
	harmoPageDetail: {
		height: '100%',
		display: 'grid',
		gridTemplateColumns: 'repeat(10, 1fr)',
		gridTemplateRows: 'auto auto auto auto 1fr',
		rowGap: '10px',
		columnGap: '10px',
	},
	span2: {
		gridColumn: 'span 2',
	},
	span4: {
		gridColumn: 'span 4',
	},
	span6: {
		gridColumn: 'span 6',
	},
	span10: {
		gridColumn: 'span 10',
	},
	status: {
		gridColumn: 'span 2',
		'& .Mui-disabled': {
			color: 'unset',
		},
	},
	ignoreBrand: {
		gridColumn: 'span 2',
		alignSelf: 'flex-end',
	},
	hideUnmatched: {
		gridColumn: 'span 2',
		alignSelf: 'flex-end',
	},
	hideMatchedElsewhere: {
		gridColumn: 'span 2',
		alignSelf: 'flex-end',
	},
	transparent: {
		opacity: 0,
	},
	actions: {
		justifyContent: 'center',
	},
	tableTitle: {
		gridColumn: 'span 10',
		textAlign: 'center',
		fontSize: 'larger',
		marginBottom: 0,
	},
}));

const dataviewStore = DataviewStore.create({
	id: 'HarmoPageDetail',
	orgColumns: [
		'name',
		'packaging',
		'itemNo',
		'eancode',
		'itemGroup1',
		'itemGroup2',
		'brand',
		'description 200',
		'age',
		'vol',
		'origin',
		'container',
		'bundle',
		'amount',
		'unit',
		'harmoInfo :harmoInfo',
		'harmoPrimarySecondary :harmoPrimarySecondary',
		'id 50',
	],
	filtering: [{columns: ['name', 'itemNo', 'eancode'], value: ''}],
	sorting: {column: 'name'},
});
dataviewStore.addColumnHiderToNamed('DEFAULT', 'itemGroup1', 'itemGroup2', 'brand', 'description', 'age', 'vol', 'origin', 'container', 'bundle', 'amount', 'unit',	'id');

interface IPrevNextButtonsProps<E extends {id: number}> {
	repoAgg: {
		value: E | undefined,
		setter(id: number | undefined) : void
		repo: {
			allAsList: Array<E>,
		},
	}
}

const PrevNextButtons = observer(function PrevNextButtons<E extends {id: number}>(props: IPrevNextButtonsProps<E>) {
	const classes = useStyles();
	const {repoAgg} = props;
	const list = repoAgg.repo.allAsList;
	function onClickFn(delta: number) {
		return function onClick(e: React.MouseEvent) {
			e.preventDefault();
			const index = !repoAgg.value ? -1 : list.indexOf(repoAgg.value);
			const i = (index + delta + list.length) % list.length;
			console.log('l=' + list.length + ', ' + index + ' ' + i + ' : ' + repoAgg.value?.id);
			repoAgg.setter(list[i]?.id);
		};
	}
	if (!list.length) return <div/>;
	return <div className={classes.span2}>
		<IconButton onClick={onClickFn(-1)}><PrevIcon/></IconButton>
		<IconButton onClick={onClickFn(+1)}><NextIcon/></IconButton>
	</div>;
});

interface IHarmoPageDetailProps {
	slaveSupplierAgg: IEntityStateAgg<ISupplier>
	masterItem: SupplierItemExtended|undefined
	setMasterItemId(masterItemId: number) : void
	harmonizationRepo: IHarmonizationRepo
	registeredSupplierRepo: IEntityStore<ISupplier>
}

const HarmoPageDetail = observer(function HarmoPageDetail(props: IHarmoPageDetailProps) {
	const theme = useTheme();
	const classes = useStyles();
	const {slaveSupplierAgg, masterItem, setMasterItemId, harmonizationRepo } = props;
	const omnistore = useOmnistore();
	const multirepo = omnistore.multirepo;
	const itemSearchAgg = useStringStateAgg('');
	const ignoreBrandAgg = useBooleanStateAgg();
	const hideUnmatchedAgg = useBooleanStateAgg();
	const hideMatchedElsewhereAgg = useBooleanStateAgg();

	const slaveSupplierRepo = new SortedRepo(multirepo.publicSupplierRepo, mg.comparatorOn(e => e.name.toLowerCase()));
	const masterSupplier = multirepo.masterSupplierRepo.byId(props.masterItem?.supplierId || 0);
	const masterItemRepo = new SortedRepo(multirepo.supplierItemRepo(masterSupplier?.id || 0), mg.comparatorOn(e => e.name.toLowerCase()));
	const slaveItemRepo = SupplierItemExtendedRepo.newRepo('HarmoPageDetail', multirepo, slaveSupplierRepo.length,
		!slaveSupplierAgg.value ? [] : [multirepo.supplierItemRepo(slaveSupplierAgg.id)]);
	const filteredRepo = (() => {
		const masterBrand = ignoreBrandAgg.value ? undefined : multirepo.brandRepo.byId(masterItem?.brandId);
		const filterOnBrand = filterOnBrandFn(masterBrand);
		const filterOnMatchedElsewhere = !hideMatchedElsewhereAgg.value ? true
			: (e: SupplierItemExtended) : boolean => e.state==='DELETED' || e.harmonization?.masterItemId === masterItem?.id;
		const filterOnUnmatched = !hideUnmatchedAgg.value ? true : (e: SupplierItemExtended) : boolean => e.state!=='DELETED';
		return new FilterRepo(slaveItemRepo, filterOnBrand, filterOnMatchedElsewhere, filterOnUnmatched);
	})();
	const transparentUnlessSupplier = slaveSupplierAgg.value ? '' : classes.transparent;
	const lineStyle = (entity: IEntity) => {
		const state = (entity as any)?.state;
		switch (state) {
		case 'PRIMARY': return {color: (theme.palette as any).special.main};
		case 'SECONDARY': return {color: theme.palette.primary.main};
		}
		return undefined;
	};

	useEffect(() => {
		if (hideMatchedElsewhereAgg.value) hideUnmatchedAgg.falseHandler();
	}, [hideMatchedElsewhereAgg.value]);
	useEffect(() => {
		dataviewStore.setFirstFilteringValue(itemSearchAgg.value);
	}, [itemSearchAgg.value]);
	useEffect(() => {
		if (slaveSupplierAgg.value) slaveItemRepo.fetch();
	}, [slaveSupplierAgg.value]);

	return <Observer>{() =>
		<div className={classes.harmoPageDetail}>
			<TextField select className={classes.span6}
				label={transl('selectMasterItem')}
				value={masterItem?.id || ''}
				error={!masterItem}
				onChange={e => setMasterItemId(+e.target.value)}
				InputLabelProps={{shrink: true}}
			>
				{masterItemRepo.allAsList.map(x => <MenuItem value={x.id} key={x.id}>{x.name} — {x.packaging} {x.eancode ? '— EAN: ' + x.eancode : ''}</MenuItem>)}
			</TextField>
			<PrevNextButtons repoAgg={{value: masterItemRepo.byId(masterItem?.id), setter: setMasterItemId, repo: masterItemRepo}}/>
			<TextField disabled className={classes.status}
				label={transl('harmoStatus')}
				value={masterItem?.harmoStatus ?? ''}
			/>

			<TextField select className={classes.span6}
				label={transl('selectSupplier')}
				value={slaveSupplierAgg.id || ''}
				error={!slaveSupplierAgg.id}
				onChange={e => slaveSupplierAgg.setter(+e.target.value)}
				InputLabelProps={{shrink: true}}
			>
				{slaveSupplierRepo.allAsList.map(x => <MenuItem value={x.id} key={x.id}>{x.name}</MenuItem>)}
			</TextField>
			<PrevNextButtons repoAgg={slaveSupplierAgg}/>
			<TextField disabled className={classes.status}
				label={transl('harmoStatus')}
				value={harmonizationRepo.countBySupplierId(slaveSupplierAgg.id) + '/' + slaveItemRepo.allAsList.length}
			/>

			<TextField className={mg.clsn(classes.span4, transparentUnlessSupplier)}
				label={transl('nameAndItemNoAndEancode')}
				value={itemSearchAgg.value}
				onChange={itemSearchAgg.changeHandler}
			/>
			<FormControl className={mg.clsn(classes.hideUnmatched, transparentUnlessSupplier, hideMatchedElsewhereAgg.value || !omnistore.reoctoStore.isTester ? classes.transparent : '')}>
				<FormControlLabel
					label={transl('hideUnmatched')}
					control={<Checkbox name={name} checked={hideUnmatchedAgg.value} color="default" onChange={hideUnmatchedAgg.toggleHandler}/>}
				/>
			</FormControl>
			<FormControl className={mg.clsn(classes.ignoreBrand, transparentUnlessSupplier)}>
				<FormControlLabel
					label={transl('ignoreBrand')}
					control={<Checkbox name={name} checked={ignoreBrandAgg.value} color="default" onChange={ignoreBrandAgg.toggleHandler}/>}
				/>
			</FormControl>
			<FormControl className={mg.clsn(classes.hideMatchedElsewhere, transparentUnlessSupplier)}>
				<FormControlLabel
					label={transl('hideMatchedElsewhere')}
					control={<Checkbox name={name} checked={hideMatchedElsewhereAgg.value} color="default" onChange={hideMatchedElsewhereAgg.toggleHandler}/>}
				/>
			</FormControl>

			{<h5 className={classes.tableTitle}>{transl('searchResults')}</h5>}
			<OcDataview state={dataviewStore} repo={filteredRepo} className={classes.span10} lineStyle={lineStyle} stickyTopEntity={masterItem}/>
		</div>
	}</Observer>;
});

export const HarmoPageDialog = observer(function(props: IHarmoPageDetailProps) {
	const classes = useStyles();
	const omnistore = useOmnistore();
	const {masterItem, setMasterItemId} = props;
	const masterSupplier = omnistore.multirepo.masterSupplierRepo.byId(props.masterItem?.supplierId);
	const locationStore = omnistore.locationStore;
	const masterItemDesc = masterItem ? masterItem.name + ' — ' + masterItem.packaging : '';

	const onClose = () => setMasterItemId(0);
	return <Dialog open={!!masterItem && !!masterSupplier?.hasMasterCatalog} className={classes.harmoPageDialog}>
		<DialogTitle disableTypography>
			{locationStore.breadcrumbsOf({masterId: masterSupplier?.id ?? 0}, [masterItemDesc])}
			<IconButton className={classes.closeButton} onClick={onClose}><CloseIcon/></IconButton>
		</DialogTitle>
		<DialogContent>
			<HarmoPageDetail {...props}/>
		</DialogContent>
	</Dialog>;
});
